import { Fragment, useState, useEffect } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import {
  CheckIcon,
  ChevronUpDownIcon,
  BuildingOfficeIcon,
  XMarkIcon,
  PlusIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  StarIcon
} from '@heroicons/react/20/solid';
import { StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline';
import { useBrand } from '@/contexts/BrandContext';
import { useNavigate } from 'react-router-dom';

const MAX_RECENT_BRANDS = 3;

export function BrandSelector() {
  const navigate = useNavigate();
  const { selectedBrand, brands, isLoading, selectBrand, clearBrandSelection } = useBrand();
  const [searchQuery, setSearchQuery] = useState('');
  const [recentBrands, setRecentBrands] = useState<string[]>([]);
  const [favoriteBrands, setFavoriteBrands] = useState<string[]>([]);

  // Load recent and favorite brands from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('recentBrands');
    if (stored) {
      setRecentBrands(JSON.parse(stored));
    }
    const favorites = localStorage.getItem('favoriteBrands');
    if (favorites) {
      setFavoriteBrands(JSON.parse(favorites));
    }
  }, []);

  // Update recent brands when selection changes
  useEffect(() => {
    if (selectedBrand) {
      setRecentBrands(prev => {
        const updated = [selectedBrand.id, ...prev.filter(id => id !== selectedBrand.id)].slice(0, MAX_RECENT_BRANDS);
        localStorage.setItem('recentBrands', JSON.stringify(updated));
        return updated;
      });
    }
  }, [selectedBrand]);

  const toggleFavorite = (brandId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setFavoriteBrands(prev => {
      const updated = prev.includes(brandId)
        ? prev.filter(id => id !== brandId)
        : [...prev, brandId];
      localStorage.setItem('favoriteBrands', JSON.stringify(updated));
      return updated;
    });
  };

  // Filter brands based on search
  const filteredBrands = brands.filter(brand =>
    searchQuery === '' ||
    brand.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    brand.industry.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get recent brand objects
  const recentBrandObjects = recentBrands
    .map(id => brands.find(b => b.id === id))
    .filter(Boolean);

  // Get favorite brand objects
  const favoriteBrandObjects = favoriteBrands
    .map(id => brands.find(b => b.id === id))
    .filter(Boolean);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-white/60">
        <BuildingOfficeIcon className="h-5 w-5 animate-pulse" />
        <span>Loading brands...</span>
      </div>
    );
  }

  if (brands.length === 0) {
    return (
      <button
        onClick={() => navigate('/onboarding')}
        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary-400 hover:text-primary-300 hover:bg-white/[0.04] rounded-lg transition-colors"
      >
        <PlusIcon className="h-5 w-5" />
        <span>Add Brand</span>
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Listbox value={selectedBrand?.id || ''} onChange={selectBrand}>
        <div className="relative">
          <Listbox.Button className="relative w-72 cursor-pointer rounded-lg bg-white/[0.04] py-2.5 pl-3 pr-10 text-left shadow-sm ring-1 ring-inset ring-white/20 hover:ring-white/40 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:text-sm sm:leading-6 transition-all">
            <span className="flex items-center gap-2">
              {selectedBrand ? (
                <>
                  <div
                    className="h-4 w-4 rounded-full flex-shrink-0 ring-2 ring-white shadow-sm"
                    style={{ backgroundColor: selectedBrand.primaryColor }}
                  />
                  <div className="flex-1 min-w-0">
                    <span className="block truncate font-semibold text-white">{selectedBrand.name}</span>
                    <span className="block text-xs text-white/60 truncate">{selectedBrand.industry}</span>
                  </div>
                </>
              ) : (
                <>
                  <BuildingOfficeIcon className="h-5 w-5 text-white/60" />
                  <span className="block truncate text-white font-medium">All Brands</span>
                </>
              )}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon className="h-5 w-5 text-white/60" aria-hidden="true" />
            </span>
          </Listbox.Button>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute z-50 mt-2 w-96 overflow-hidden rounded-lg bg-gray-900/95 text-base shadow-xl ring-1 ring-white/20 focus:outline-none sm:text-sm">
              {/* Search bar */}
              {brands.length > 5 && (
                <div className="p-2 border-b border-white/10">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/60" />
                    <input
                      type="text"
                      placeholder="Search brands..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm bg-white/[0.04] border border-white/[0.06] text-white placeholder-white/50 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
              )}

              {/* Quick actions */}
              <div className="p-2 border-b border-white/10 bg-white/[0.04]">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-white/60 uppercase tracking-wide">Quick Actions</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/onboarding');
                    }}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-400 hover:text-primary-300 hover:bg-white/[0.04] rounded transition-colors"
                  >
                    <PlusIcon className="h-3 w-3" />
                    New Brand
                  </button>
                </div>
              </div>

              <div className="max-h-80 overflow-y-auto">
                {/* All Brands option */}
                <div className="py-1">
                  <Listbox.Option
                    value={''}
                    className={({ active }) =>
                      `relative cursor-pointer select-none py-2.5 pl-3 pr-9 ${
                        active ? 'bg-white/[0.04]' : ''
                      }`
                    }
                  >
                    {({ selected }) => (
                      <>
                        <div className="flex items-center gap-2">
                          <BuildingOfficeIcon className="h-5 w-5 text-white/60" />
                          <span className={`block truncate ${selected ? 'font-semibold text-white' : 'font-medium text-white/80'}`}>
                            All Brands
                          </span>
                          <span className="text-xs text-white/50">({brands.length} total)</span>
                        </div>
                        {selected && (
                          <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-primary-400">
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        )}
                      </>
                    )}
                  </Listbox.Option>
                </div>

                {/* Favorite brands */}
                {favoriteBrandObjects.length > 0 && (
                  <>
                    <div className="px-3 py-2 bg-amber-500/10 border-y border-amber-400/20">
                      <div className="flex items-center gap-1.5">
                        <StarIcon className="h-3.5 w-3.5 text-amber-400" />
                        <span className="text-xs font-semibold text-amber-400 uppercase tracking-wide">Favorites</span>
                      </div>
                    </div>
                    <div className="py-1">
                      {favoriteBrandObjects.map((brand: any) => (
                        <Listbox.Option
                          key={brand.id}
                          value={brand.id}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2.5 pl-3 pr-9 ${
                              active ? 'bg-white/[0.04]' : ''
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <div className="flex items-center gap-2">
                                <div
                                  className="h-4 w-4 rounded-full flex-shrink-0 ring-2 ring-white shadow-sm"
                                  style={{ backgroundColor: brand.primaryColor }}
                                />
                                <div className="flex-1 min-w-0">
                                  <span className={`block truncate ${selected ? 'font-semibold text-white' : 'font-medium text-white/80'}`}>
                                    {brand.name}
                                  </span>
                                  <span className="text-xs text-white/60">{brand.industry}</span>
                                </div>
                                <button
                                  onClick={(e) => toggleFavorite(brand.id, e)}
                                  className="p-1 hover:bg-amber-500/20 rounded transition-colors"
                                >
                                  <StarIcon className="h-4 w-4 text-amber-400" />
                                </button>
                              </div>
                              {selected && (
                                <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-primary-400">
                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                </span>
                              )}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </div>
                  </>
                )}

                {/* Recent brands */}
                {recentBrandObjects.length > 0 && (
                  <>
                    <div className="px-3 py-2 bg-blue-500/10 border-y border-blue-400/20">
                      <div className="flex items-center gap-1.5">
                        <ClockIcon className="h-3.5 w-3.5 text-blue-400" />
                        <span className="text-xs font-semibold text-blue-400 uppercase tracking-wide">Recent</span>
                      </div>
                    </div>
                    <div className="py-1">
                      {recentBrandObjects.map((brand: any) => {
                        if (favoriteBrands.includes(brand.id)) return null; // Skip if already in favorites
                        return (
                          <Listbox.Option
                            key={brand.id}
                            value={brand.id}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-2.5 pl-3 pr-9 ${
                                active ? 'bg-white/[0.04]' : ''
                              }`
                            }
                          >
                            {({ selected }) => (
                              <>
                                <div className="flex items-center gap-2">
                                  <div
                                    className="h-4 w-4 rounded-full flex-shrink-0 ring-2 ring-white shadow-sm"
                                    style={{ backgroundColor: brand.primaryColor }}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <span className={`block truncate ${selected ? 'font-semibold text-white' : 'font-medium text-white/80'}`}>
                                      {brand.name}
                                    </span>
                                    <span className="text-xs text-white/60">{brand.industry}</span>
                                  </div>
                                  <button
                                    onClick={(e) => toggleFavorite(brand.id, e)}
                                    className="p-1 hover:bg-white/[0.04] rounded transition-colors"
                                  >
                                    <StarOutlineIcon className="h-4 w-4 text-white/60" />
                                  </button>
                                </div>
                                {selected && (
                                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-primary-400">
                                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                  </span>
                                )}
                              </>
                            )}
                          </Listbox.Option>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* All brands */}
                <div className="px-3 py-2 bg-white/[0.04] border-y border-white/10">
                  <span className="text-xs font-semibold text-white/60 uppercase tracking-wide">
                    All Brands {searchQuery && `(${filteredBrands.length} results)`}
                  </span>
                </div>
                <div className="py-1">
                  {filteredBrands.length === 0 ? (
                    <div className="px-3 py-6 text-center text-sm text-white/60">
                      No brands found matching "{searchQuery}"
                    </div>
                  ) : (
                    filteredBrands.map((brand) => (
                      <Listbox.Option
                        key={brand.id}
                        value={brand.id}
                        className={({ active }) =>
                          `relative cursor-pointer select-none py-2.5 pl-3 pr-9 ${
                            active ? 'bg-white/[0.04]' : ''
                          }`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <div className="flex items-center gap-2">
                              <div
                                className="h-4 w-4 rounded-full flex-shrink-0 ring-2 ring-white shadow-sm"
                                style={{ backgroundColor: brand.primaryColor }}
                              />
                              <div className="flex-1 min-w-0">
                                <span className={`block truncate ${selected ? 'font-semibold text-white' : 'font-medium text-white/80'}`}>
                                  {brand.name}
                                </span>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-white/60">{brand.industry}</span>
                                  {brand._count && (
                                    <span className="text-xs text-white/50">• {brand._count.campaigns} campaigns</span>
                                  )}
                                </div>
                              </div>
                              <button
                                onClick={(e) => toggleFavorite(brand.id, e)}
                                className="p-1 hover:bg-white/[0.04] rounded transition-colors"
                              >
                                {favoriteBrands.includes(brand.id) ? (
                                  <StarIcon className="h-4 w-4 text-amber-400" />
                                ) : (
                                  <StarOutlineIcon className="h-4 w-4 text-white/60" />
                                )}
                              </button>
                            </div>
                            {selected && (
                              <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-primary-400">
                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                              </span>
                            )}
                          </>
                        )}
                      </Listbox.Option>
                    ))
                  )}
                </div>
              </div>
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>

      {/* Clear selection button */}
      {selectedBrand && (
        <button
          onClick={clearBrandSelection}
          className="p-2 text-white/60 hover:text-white rounded-lg hover:bg-white/[0.04] transition-colors"
          title="View all brands"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}
